import { Plus, X } from "lucide-react";
import { useMemo, useState } from "react";

import { EmptyState } from "@/components/common/EmptyState";
import { FloatingButton } from "@/components/common/FloatingButton";
import { SearchInput } from "@/components/common/SearchInput";
import { StatCard } from "@/components/common/StatCard";
import { StockItemList, type StockRow } from "@/components/common/StockItemList";
import { BackButton } from "@/components/ui/back-button";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { useCurrency } from "@/features/accounts/hooks/useCurrency";
import ProvisioningPage from "@/features/provision-stocks/ProvisioningPage";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { stockStatus } from "@/lib/expiry";
import { currencySymbol, formatAmount, plural } from "@/lib/format";
import type { ProvisionStockStateDto } from "@/types/stock.types";

type Filter = "all" | "expiring" | "out";

const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: "all", label: "Toutes" },
  { value: "expiring", label: "Bientôt périmées" },
  { value: "out", label: "Épuisées" },
];

export default function ProvisionStockDetailPage({
  stock,
  onBack,
}: {
  stock: ProvisionStockStateDto;
  onBack: () => void;
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [isProvisioning, setIsProvisioning] = useState(false);
  const { currency } = useCurrency();
  const isDesktop = useIsDesktop();

  const rows: StockRow[] = useMemo(
    () =>
      stock.items.map((item) => ({
        id: item.provision.id,
        name: item.provision.name,
        measurementUnit: item.provision.measurementUnit,
        quantity: item.quantity,
        expiryDate: item.expiryDate,
      })),
    [stock.items],
  );

  const counts = useMemo(() => {
    let expiring = 0;
    let out = 0;
    for (const row of rows) {
      const status = stockStatus(row.quantity, row.expiryDate);
      if (status === "expiring" || status === "expired") expiring += 1;
      if (status === "out") out += 1;
    }
    return { expiring, out };
  }, [rows]);

  const visible = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return rows.filter((row) => {
      if (term && !row.name.toLowerCase().includes(term)) return false;
      const status = stockStatus(row.quantity, row.expiryDate);
      if (filter === "expiring") return status === "expiring" || status === "expired";
      if (filter === "out") return status === "out";
      return true;
    });
  }, [rows, searchTerm, filter]);

  if (isProvisioning) {
    return <ProvisioningPage stock={stock} onBack={() => setIsProvisioning(false)} />;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start gap-3">
        <BackButton onClick={onBack} />
        <PageHeader
          className="flex-1"
          title={stock.name}
          subtitle="Ce que vous avez acheté et pas encore transformé"
          action={
            isDesktop ? (
              <Button size="lg" onClick={() => setIsProvisioning(true)}>
                <Plus />
                Approvisionner
              </Button>
            ) : null
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Valeur du stock"
          value={formatAmount(stock.monetaryValue, currency)}
          unit={currencySymbol(currency)}
        />
        <StatCard label="Références suivies" value={String(rows.length)} unit={plural(rows.length, "matière")} />
        <StatCard
          label="Bientôt périmées"
          value={String(counts.expiring)}
          unit={plural(counts.expiring, "lot")}
          tone={counts.expiring > 0 ? "warning" : "default"}
        />
        <StatCard
          label="Épuisées"
          value={String(counts.out)}
          unit={plural(counts.out, "matière")}
          tone={counts.out > 0 ? "danger" : "default"}
        />
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher une matière…"
        />
        <div className="flex gap-2 overflow-x-auto">
          {FILTERS.map((option) => (
            <Button
              key={option.value}
              type="button"
              variant={filter === option.value ? "secondary" : "outline"}
              onClick={() => setFilter(option.value)}
              className="shrink-0 rounded-full"
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      {visible.length === 0 ? (
        <EmptyState
          title="Rien à afficher"
          hint={
            searchTerm
              ? "Aucune matière ne correspond à votre recherche."
              : filter === "all"
                ? "Approvisionnez ce stock pour y voir vos matières."
                : "Aucune matière dans cet état."
          }
          action={
            searchTerm ? (
              <Button type="button" variant="outline" onClick={() => setSearchTerm("")}>
                <X />
                Effacer la recherche
              </Button>
            ) : filter === "all" ? (
              <Button type="button" onClick={() => setIsProvisioning(true)}>
                <Plus />
                Approvisionner
              </Button>
            ) : null
          }
        />
      ) : (
        <StockItemList rows={visible} currency={currency} />
      )}

      {!isDesktop && <FloatingButton icon={Plus} ariaLabel="Approvisionner" onClick={() => setIsProvisioning(true)} />}
    </div>
  );
}
